import { OPEN_STATUSES } from "@/lib/support-views";
import { STALE_TICKET_DAYS } from "./thresholds";
import { daysBetween } from "./time";
import type { BrainItem, BrainKind } from "./types";

const OPEN = new Set(OPEN_STATUSES);

/** Latest admin reply per ticket. The database query excludes is_system rows. */
export function lastAdminReplies(
  rows: { issue_id: string; created_at: string; comment: string | null }[]
): Map<string, string> {
  const last = new Map<string, string>();
  for (const r of rows) {
    const prev = last.get(r.issue_id);
    if (!prev || Date.parse(r.created_at) > Date.parse(prev))
      last.set(r.issue_id, r.created_at);
  }
  return last;
}

export interface SupportDeriveInput {
  tickets: {
    id: string;
    title: string | null;
    status: string;
    assigned_to: string | null;
    archived_at: string | null;
    snoozed_until?: string | null;
    created_at: string;
    /** Latest message of any kind (trigger-maintained); null on old rows. */
    last_message_at: string | null;
    /** Read only to hide test tickets on prod (test-data.ts). */
    bookings?: { email: string | null } | null;
    properties?: { title: string | null } | null;
  }[];
  /** Latest tenant-visible admin message per ticket. null = not loaded: no stale cards. */
  lastAdminReplyAt: Map<string, string> | null;
  /** The caller's unread messages per ticket. null = not loaded: no unread cards. */
  unread: Map<string, number> | null;
}

/**
 * Ticket cards carry the ticket's own assignee (`issues.assigned_to`), so the
 * deck never reads brain_item_state for them. One ticket can deal all three.
 */
export function deriveSupportItems(
  input: SupportDeriveInput,
  now: Date
): BrainItem[] {
  const out: BrainItem[] = [];
  for (const tk of input.tickets) {
    if (
      tk.archived_at ||
      !OPEN.has(tk.status) ||
      (tk.snoozed_until != null && Date.parse(tk.snoozed_until) > now.getTime())
    )
      continue;
    const href = `/admin?tab=issues&ticket=${tk.id}`;
    const mk = (kind: BrainKind, since: string | null): BrainItem => ({
      key: `${kind}:${tk.id}`,
      kind,
      since,
      who: { label: tk.title || `#${tk.id.slice(0, 8)}`, href },
      facts: [],
      primaryHref: href,
      sourceAssigneeId: tk.assigned_to,
      refs: { ticketId: tk.id },
    });
    if (!tk.assigned_to && tk.status === "open")
      out.push(mk("ticket_unassigned", tk.created_at));
    const unread = input.unread?.get(tk.id) ?? 0;
    if (unread > 0) {
      // A fresh message on an old ticket is new: age it from the latest one.
      const it = mk("ticket_unread", tk.last_message_at ?? tk.created_at);
      it.facts = [
        {
          label: { key: "brain.fact.unread", fallback: "Unread messages" },
          value: String(unread),
        },
      ];
      out.push(it);
    }
    if (tk.status !== "waiting" && input.lastAdminReplyAt) {
      const last = input.lastAdminReplyAt.get(tk.id) ?? tk.created_at;
      if (daysBetween(last, now) >= STALE_TICKET_DAYS)
        out.push(mk("ticket_stale", last));
    }
  }
  return out;
}
